import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import Cookies from 'js-cookie';
import * as XLSX from "xlsx";
import { Modal, Button } from "react-bootstrap";
import { ChevronDown, ChevronRight, MapPin, Users, Key } from 'lucide-react';

const accentColor = "#ee373b";
const id = Cookies.get("adminLenderIdCookie");

const ZoneRegionManager = () => {
  const [zones, setZones] = useState([]);
  const [zoneDesc, setZoneDesc] = useState("");
  const [zoneUser, setZoneUser] = useState("");
  const [zonePass, setZonePass] = useState("");
  const [regionDesc, setRegionDesc] = useState("");
  const [regionUser, setRegionUser] = useState("");
  const [regionPass, setRegionPass] = useState("");
  const [selectedZone, setSelectedZone] = useState("");
  const [excelZones, setExcelZones] = useState([]);
  const [excelRegions, setExcelRegions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [expandedZones, setExpandedZones] = useState(new Set());
  const [showPasswords, setShowPasswords] = useState(false);

  // Fetch zones and regions on component mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/api/zoneRegion/zones-and-regions/${Cookies.get("adminLenderIdCookie")}`, {
        credentials: "include"
      });
      const data = await response.json();
      const processedZones = processZonesData(data);
      setZones(processedZones);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const processZonesData = (data) => {
    const zonesMap = new Map();
    data.forEach(item => {
      if (!zonesMap.has(item.zoneId)) {
        zonesMap.set(item.zoneId, {
          id: item.zoneId,
          desc: item.zoneDescription,
          user: item.zoneUserName,
          pass: item.zoneUserPassword,
          regions: []
        });
      }
      if (item.regionId) {
        zonesMap.get(item.zoneId).regions.push({
          id: item.regionId,
          desc: item.regionDescription,
          user: item.regionUserName,
          pass: item.regionUserPassword
        });
      }
    });
    return Array.from(zonesMap.values());
  };

  const addZone = async () => {
    if (!zoneDesc.trim()) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/api/zoneRegion/zones`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          lenderId: Cookies.get("adminLenderIdCookie"),
          zoneDescription: zoneDesc,
          userName: zoneUser,
          userPassword: zonePass,
          isActive: 1
        }),
        credentials: "include"
      });
      if (response.ok) {
        await fetchData(); // Refetch the data
        setZoneDesc("");
        setZoneUser("");
        setZonePass("");
      }
    } catch (error) {
      console.error("Error adding zone:", error);
    }
  };

  const addRegion = async (zoneId) => {
    if (!regionDesc.trim()) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/api/zoneRegion/regions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          lenderId: Cookies.get("adminLenderIdCookie"),
          zoneId: zoneId,
          regionDescription: regionDesc,
          userName: regionUser,
          userPassword: regionPass,
          isActive: 1
        }),
        credentials: "include"
      });
      if (response.ok) {
        await fetchData(); // Refetch the data
        setRegionDesc("");
        setRegionUser("");
        setRegionPass("");
      }
    } catch (error) {
      console.error("Error adding region:", error);
    }
  };

  const handleExcel = (e) => {
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: "binary" });
      const zonesSheet = wb.Sheets[wb.SheetNames[0]];
      const regionsSheet = wb.Sheets[wb.SheetNames[1]];
      const parsedZones = XLSX.utils.sheet_to_json(zonesSheet);
      const parsedRegions = XLSX.utils.sheet_to_json(regionsSheet);
      setExcelZones(parsedZones);
      setExcelRegions(parsedRegions);
      setShowModal(true);
    };
    reader.readAsBinaryString(file);
  };

  const confirmUpload = async () => {
    try {
      // Prepare zones data
      const zonesToUpload = excelZones.map(z => ({
        lenderId: Cookies.get("adminLenderIdCookie"),
        zoneDescription: z["Zone Name"],
        userName: z["User Name"],
        userPassword: z["Password"],
        isActive: 1
      }));
      // Prepare regions data with zoneDescription instead of zoneId
      const regionsToUpload = excelRegions.map(r => ({
        lenderId: Cookies.get("adminLenderIdCookie"),
        zoneDescription: r["Zone"],
        regionDescription: r["Region Name"],
        userName: r["User Name"],
        userPassword: r["Password"],
        isActive: 1
      }));
      await fetch(`${process.env.REACT_APP_API_ENDPOINT}/api/zoneRegion/bulk-upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          zones: zonesToUpload,
          regions: regionsToUpload
        }),
        credentials: "include"
      });
      // Refetch the data to update the table
      await fetchData();
      setShowModal(false);
    } catch (error) {
      console.error("Error bulk upload:", error);
    }
  };

  const toggleZone = (zoneId) => {
    const newExpanded = new Set(expandedZones);
    if (newExpanded.has(zoneId)) {
      newExpanded.delete(zoneId);
    } else {
      newExpanded.add(zoneId);
    }
    setExpandedZones(newExpanded);
  };

  const maskPassword = (password) => {
    return showPasswords ? password : '••••••••';
  };

  return (
    <div className="p-4" style={{ backgroundColor: "#fff", maxHeight: "90vh", overflowY:"scroll", scrollbarColor:"#ee373b #ffffff" }}>
      <style>{`
        #model-excel{
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding-top: 2vh;
        }
        @keyframes fadeSlide {
          0% { opacity: 0; transform: translateY(-10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .card {
          animation: fadeSlide 0.5s ease-in-out;
        }
        @media (max-width: 768px) {
          .form-row {
            flex-direction: column !important;
          }
        }
      `}</style>
      <div className="d-flex justify-content-center align-items-center mb-4">
        <h5 className="text-danger bg-white text-center fw-bold">Zone and Region Manager</h5>
      </div>
      <div className="row mb-4">
        {/* Add Zone */}
        <div className="col-md-6">
          <div className="card p-3 border" style={{ borderColor: accentColor }}>
            <h6 style={{ color: accentColor, backgroundColor: "white" }}>Add Zone</h6>
            <input
              className="form-control mb-2"
              placeholder="Zone Name"
              value={zoneDesc}
              onChange={(e) => setZoneDesc(e.target.value)}
            />
            <div className="d-flex mb-2">
              <input
                type="text"
                className="form-control me-2"
                placeholder="UserName"
                value={zoneUser}
                onChange={(e) => setZoneUser(e.target.value)}
              />
              <input
                type="password"
                className="form-control"
                placeholder="Password"
                value={zonePass}
                onChange={(e) => setZonePass(e.target.value)}
              />
            </div>
            <div className="d-flex justify-content-center">
              <button className="btn btn-sm text-white w-25" style={{ backgroundColor: accentColor, borderRadius: "5px" }} onClick={addZone}>
                Add
              </button>
            </div>
          </div>
        </div>
        {/* Upload Excel */}
        <div className="col-md-6">
          <div className="card p-3 border" style={{ borderColor: accentColor }}>
            <h6 style={{ color: accentColor, backgroundColor: "white" }}>Upload Excel</h6>
            <input type="file" accept=".xlsx,.xls" onChange={handleExcel} className="form-control mb-2" />
          </div>
        </div>
      </div>
      {/* Tree View */}
      <div className="bg-white rounded-lg shadow-sm border">
        {zones.map((zone) => (
          <div key={zone.id} className="border-b last:border-b-0">
            <div
              className="d-flex align-items-center p-3 justify-content-between"
              style={{ cursor: "pointer", borderBottom: "1px solid #eee" }}
              onClick={() => toggleZone(zone.id)}
            >
              <div className="d-flex align-items-center gap-3 flex-1">
                {expandedZones.has(zone.id) ? (
                  <ChevronDown size={20} className="text-gray-600" />
                ) : (
                  <ChevronRight size={20} className="text-gray-600" />
                )}
                <MapPin size={20} color={"#ee373b"} />
                <div>
                  <div className="font-semibold text-gray-800">
                    {zone.desc} (ID: {zone.id})
                  </div>
                  <div className="text-sm text-gray-600">
                    {zone.regions.length} region{zone.regions.length !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
              <div className="text-sm text-gray-500">
                User: {zone.user} | Pass: {maskPassword(zone.pass)}
              </div>
            </div>
            {expandedZones.has(zone.id) && (
              <div className="ps-5 pe-4 pb-4">
                {/* Regions List */}
                {zone.regions.length === 0 ? (
                  <div className="text-gray-500 italic py-2">No regions assigned</div>
                ) : (
                  <div className="d-flex flex-column gap-2 py-2">
                    {zone.regions.map((region) => (
                      <div
                        key={region.id}
                        className="d-flex align-items-center justify-content-between p-3 bg-light rounded-lg"
                        style={{ borderLeft: `3px solid ${accentColor}` }}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <Users size={16} className="text-success" />
                          <div>
                            <div className="font-medium text-gray-800">
                              {region.desc} (ID: {region.id})
                            </div>
                          </div>
                        </div>
                        <div className="text-sm text-gray-500">
                          User: {region.user} | Pass: {maskPassword(region.pass)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {/* Add Region Form */}
                <div className="card p-3 border mb-3" style={{ borderColor: accentColor }}>
                  <h6 style={{ color: accentColor, backgroundColor: "white" }}>Add Region to {zone.desc}</h6>
                  <input
                    className="form-control mb-2"
                    placeholder="Region Name"
                    value={regionDesc}
                    onChange={(e) => setRegionDesc(e.target.value)}
                  />
                  <div className="d-flex mb-2">
                    <input
                      type="text"
                      className="form-control me-2"
                      placeholder="UserName"
                      value={regionUser}
                      onChange={(e) => setRegionUser(e.target.value)}
                    />
                    <input
                      type="password"
                      className="form-control"
                      placeholder="Password"
                      value={regionPass}
                      onChange={(e) => setRegionPass(e.target.value)}
                    />
                  </div>
                  <div className="d-flex justify-content-center">
                    <button
                      className="btn btn-sm text-white w-25"
                      style={{ backgroundColor: accentColor, borderRadius: "5px" }}
                      onClick={() => addRegion(zone.id)}
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="mt-3 text-sm text-gray-600">
        Total: {zones.length} zones, {zones.reduce((acc, zone) => acc + zone.regions.length, 0)} regions
      </div>
      {/* Excel Preview Modal */}
      <Modal show={showModal} onHide={() => { setShowModal(false) }} size="lg" id="model-excel">
        <Modal.Header style={{ borderTopLeftRadius: "10px", borderTopRightRadius: "10px", backgroundColor: accentColor, color: "white" }}>
          <Modal.Title>Preview Excel Data</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ borderLeft: "2px solid #ee373b", borderRight: "2px solid #ee373b" }}>
          <h6 style={{ color: accentColor, backgroundColor: "white" }}>Zones</h6>
          <div style={{ maxHeight: "150px", overflowY: "scroll", scrollbarColor: "#ee373b #ffffff" }}>
            <table className="table table-sm">
              <thead>
                <tr>
                  <th style={{ position: "sticky", top: "0" }}>Zone Name</th>
                  <th style={{ position: "sticky", top: "0" }}>User Name</th>
                  <th style={{ position: "sticky", top: "0" }}>Password</th>
                </tr>
              </thead>
              <tbody>
                {excelZones.map((z, i) => (
                  <tr key={i}>
                    <td>{z["Zone Name"]}</td>
                    <td>{z["User Name"]}</td>
                    <td>{z["Password"]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <h6 style={{ color: accentColor, backgroundColor: "white" }}>Regions</h6>
          <div style={{ maxHeight: "150px", overflowY: "scroll", scrollbarColor: "#ee373b #ffffff" }}>
            <table className="table table-sm">
              <thead>
                <tr>
                  <th style={{ position: "sticky", top: "0" }}>Zone</th>
                  <th style={{ position: "sticky", top: "0" }}>Region Name</th>
                  <th style={{ position: "sticky", top: "0" }}>User Name</th>
                  <th style={{ position: "sticky", top: "0" }}>Password</th>
                </tr>
              </thead>
              <tbody>
                {excelRegions.map((r, i) => (
                  <tr key={i}>
                    <td>{r["Zone"]}</td>
                    <td>{r["Region Name"]}</td>
                    <td>{r["User Name"]}</td>
                    <td>{r["Password"]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button style={{ backgroundColor: accentColor, borderColor: accentColor }} onClick={confirmUpload}>
            Confirm Upload
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ZoneRegionManager;